import { Attr, IndexedMesh, TypedArray } from "./indexed-mesh";
import { IndexedMeshWriter } from "./indexed-mesh-writer";
import { QUARTER_TURN } from "../math/constants";
import { Transform } from "./transform";
import { Vector3 } from "three";
import { VertexList } from "./vertex-list";


type SweptMeshAttrs = {
    normal: Attr,
    uv: Attr,
}

/**
 * The SweptMesh moves a 2D polygon along a 2D polygonal plan. Both the profile
 * and the plan are represented by the EdgeList class. The really important
 * reason this exists is that the resulting mesh includes normals and UVs
 * derived from the underlying polygons.
 *
 * Think of the plan object as blueprint drawing of the basic shape of the
 * resulting mesh in the ground plane. The profile object establishes the
 * elevation of the object. As a result, the (x, y) coordinates of the plan
 * object will lay in the XZ plane of the output object. The X axis of the
 * profile object will point in a direction perpendicular to the current edge of
 * the plan such that +X in the elevation aligns with the outer face of the
 * plan. The Y axis of the profile object will become the Y axis of the
 * resulting 3D object.
 *
 * Note that the plan's transfer from XY to XZ inverts the Z component so that
 * the plan lays on the ground plane as seen from above, i.e. with +Z pointing
 * toward the bottom of the view. This is the same transformation we have to do
 * with floor plans in the main CPS app.
 *
 * The vertex normal of the resulting mesh points in the direction of the
 * profile object's vertex normal, rotated by the vertex normal of the plan
 * object.
 *
 * The U coordinate of each vertex is given by the vertex's distance around the
 * perimeter of the profile. Similarly, the V coordinate is given by the
 * vertex's distance around the perimeter of the plan.
 */
export class SweptMesh<T extends SweptMeshAttrs> extends IndexedMeshWriter<T> {
    profile: VertexList;
    plan: VertexList;

    planCount: number;
    profileCount: number;

    // Track the index into the position and normal attribute data between
    // calls to writeProfile
    p: number = 0;
    n: number = 0;
    u: number = 0;

    // Some scratch variables
    t: Transform = new Transform();
    tnorm: Transform = new Transform();
    v: Vector3 = new Vector3();

    constructor(profile: VertexList, plan: VertexList) {
        super();
        this.profile = profile;
        this.plan = plan;
    }

    computeAttributeCounts(): IndexedMesh<T> {
        const mesh = new IndexedMesh({
            normal: new Attr(3),
            uv: new Attr(2),
        });

        // If either the profile or the plan are smaller than two vertices,
        // that would mean one of them has no edges. With no edges, we have
        // no quads and no triangles, so our data length is zero. The
        // attributes default to having zero counts, so our work here is done.
        if(this.profile.length < 2 || this.plan.length < 2) {
            return mesh as IndexedMesh<T>;
        }

        // Topoligically, the swept mesh is a grid. But this is complicated by
        // vertex positions that have different normals on the incomming edge
        // versus the outgoing edge. These must be represented as two different
        // entries in the position array, despite having identical coordinates,
        // so that the corresponding normal element can differ at the two
        // positions.
        
        // Since we're basically a grid, the count of vertices should be
        // greater than width times height, i.e. the vertex count of the
        // profile times the vertex count of the plan. For every discontinuity
        // in the normals in either vertex list, we double count that vertex.

        // Start with the minimum possible count of vertices
        let
            profileCount = this.profile.length,
            planCount = this.plan.length;

        // Double count wherever we see a discontinuity
        for(const v of this.profile) {
            if(v.inorm != v.onorm) profileCount++;
        }
        for(const v of this.plan) {
            if(v.inorm != v.onorm) planCount++;
        }
        this.profileCount = profileCount;
        this.planCount = planCount;

        const
            vertices = profileCount * planCount,

            // Irrespective of how many duplicated vertices we have, the grid
            // will always have the same number of faces. The number of edges
            // in the profile times the number of edges in the plan gives us
            // the number of quads, which we double to obtain the number of
            // triangles.
            triangles = 2
                * (this.profile.closed ? this.profile.length : this.profile.length - 1)
                * (this.plan.closed ? this.plan.length : this.plan.length - 1);
            
        mesh.index.count = triangles * 3;
        mesh.position.count = vertices;
        mesh.attrs.normal.count = vertices;
        mesh.attrs.uv.count = vertices;

        return mesh as IndexedMesh<T>;
    }

    computeMeshData(mesh: IndexedMesh<T>): void {
        // As above, if we don't have any edges, then we have no data, so our
        // work here is done.
        if(this.profile.length < 2 || this.plan.length < 2) return;

        // Reset the data indices to 0 in case we're being reused
        this.p = this.n = this.u = 0;

        // For each vertex in the plan:
        //
        // - With a clean transformation matrix, shear the profile so that each
        // Z coordinate moves in proportion to its X component by an angle
        // matching the bisecting angle at that vertex of the plan. Then move
        // to the position of the profile to the first vertex of the plan's
        // edge and rotate it to match the global rotation of the edge.
        //
        // - With another transformation matrix, repeat this for the second
        // vertex of the edge.
        //
        // - Form a quad spanning each edge of the profile transformed with
        // both of these matricies
        //
        // - The normal of each vertex is the normal of the profile transformed
        //
        // - The U coordinate (horizontal) of each vertex is its distance along
        // the perimeter of the profile.
        //
        // - The V coordinate (vertical) of each vertex is its distance around
        // the perimeter of the plan


        // First, we establish the profile vertices at the first plan vertex so
        // that we can then loop through the rest of the plan vertices
        // uniformly. In the last iteration we're going to connect back to the
        // first row of profile vertices. If there is a discontinuity in the
        // normal here we need to place two sets of vertexes, just as with any
        // other vertex on the plan.

        this.profile.computeAdditionalProperties();
        this.plan.computeAdditionalProperties();

        // The cumulative distance around the plan perimeter
        let V = 0;

        for(let vert of this.plan) {
            const edgeAngle = vert.oangle - QUARTER_TURN; // -vert.oangle + QUARTER_TURN;

            this.t.reset();
            this.t.shearXZ(-vert.halfAngle);
            this.t.rotateY(edgeAngle);
            // Note the negation of the Y coordinate as we transfer it to the
            // Z coordinate. This makes sure that the plan is interpreted as if
            // seen from above the ground plane.
            this.t.translate(vert.pos.x, 0, -vert.pos.y);

            // The normal vector itself can be used as the basis vectors for
            // the normal's rotation matrix. We want the plan vertex's normal
            // to be the rotation around Y to which we align the profile's
            // normal.
            this.tnorm.m.set(
                vert.inorm.x,  0, vert.inorm.y, 0,
                0,             1, 0,            0,
                -vert.inorm.y, 0,  vert.inorm.x, 0,
                0,             0, 0,            1,
            );

            // NB: writeProfile reads the state of this.t and this.tnorm
            this.writeProfile(mesh, V);

            // Output another rank of vertecies in the same position if they
            // need a distinct outgoing normal
            if(vert.inorm != vert.onorm) {
                this.tnorm.m.set(
                    vert.onorm.x,  0, vert.onorm.y, 0,
                    0,             1, 0,            0,
                    -vert.onorm.y, 0,  vert.onorm.x, 0,
                    0,             0, 0,            1,
                );
                this.writeProfile(mesh, V);
            }

            V += vert.olength;
        }

        this.writeIndex(mesh);
    }

    writeProfile(mesh: IndexedMesh<T>, V: number) {
        const
            pos = mesh.position.data,
            norm = mesh.attrs.normal.data,
            uv = mesh.attrs.uv.data;

        // U tracks the cumulative distance around the profile perimiter
        let U = 0;
        for(const vert of this.profile) {
            this.v.set(vert.pos.x, vert.pos.y, 0);
            this.t.apply(this.v);

            pos[this.p++] = this.v.x;
            pos[this.p++] = this.v.y;
            pos[this.p++] = this.v.z;
            uv[this.u++] = U;
            uv[this.u++] = V;
            if(vert.onorm != vert.inorm) {
                pos[this.p++] = this.v.x;
                pos[this.p++] = this.v.y;
                pos[this.p++] = this.v.z;
                uv[this.u++] = U;
                uv[this.u++] = V;
            }

            U += vert.olength;

            this.v.set(vert.inorm.x, vert.inorm.y, 0);
            this.tnorm.apply(this.v);

            norm[this.n++] = this.v.x;
            norm[this.n++] = this.v.y;
            norm[this.n++] = this.v.z;
            if(vert.onorm != vert.inorm) {
                this.v.set(vert.onorm.x, vert.onorm.y, 0);
                this.tnorm.apply(this.v);
                norm[this.n++] = this.v.x;
                norm[this.n++] = this.v.y;
                norm[this.n++] = this.v.z;
            }
        }
    }

    /**
     * Writes the entire index, which is done without reference to the 
     * measurements of the mesh, just by knowing it has a grid structure.
     */
    writeIndex(mesh: IndexedMesh<T>) {
        const
            profile = this.profile,
            plan = this.plan,
            idx = mesh.index.data;

        let
            i = 0,
            r = 0,
            s = this.profileCount;

        let vert = plan.first;
        while(vert.next != null) {
            if(vert.onorm != vert.inorm) {
                r += this.profileCount;
                s += this.profileCount;
            }

            profileIdx(profile, idx, i, r, s);
            i += 6 * (this.profile.length - 1);
            r += this.profileCount;
            s += this.profileCount;

            vert = vert.next;
        }

        // If we have a final edge returning to the first vertex, then we
        // handle that here as a special case
        if(this.plan.closed) {
            if(this.plan.last.inorm != this.plan.last.onorm) {
                r += this.profileCount;
            }

            // By now, r should be the last row of profile vertices and the
            // first row of profile vertices was laid down at the start at 0.
            profileIdx(profile, idx, i, r, 0);
        }

    }
}

/**
 * Writes a row of quads spanning the given profile. This advances `i` by 6
 * times the number of vertices in the profile list. It advances `r` and `s` by
 * the SweptMesh's profileCount, i.e. the number of vertex elements in the
 * profile including duplicates at normal discontinuities.
 */
const profileIdx = (profile: VertexList, idx: TypedArray, i: number, r: number, s: number) => {
    // this loop will iterate n - 1 times where n is the number of vertices,
    // i.e. once per edge in the profile
    let vert = profile.first;
    while(vert.next != null) {
        // If we have a normal discontinuity in the profile, then we increment
        // the `r` and `s` counters so that we do not place degenerate
        // triangles on the edge that has the discontinuity
        if(vert.onorm != vert.inorm) {
            r++; s++;
        }

        idx[i++] = r;
        idx[i++] = s;
        idx[i++] = s + 1;

        idx[i++] = r;
        idx[i++] = s + 1;
        idx[i++] = r + 1;
        r++; s++;

        vert = vert.next;
    }
}

